<script setup>
import { ref, watch, computed, inject } from 'vue'
import { useLogsStore } from '@/store/logs'
import { useRoute } from 'vue-router'

const route = useRoute()
const windowIsTooSmall = inject('windowTooSmall')
const loading = computed(() => { return logsStore.loadingStuff })
const logsStore = useLogsStore()
const showDetailModal = ref(false)
const tabs = ref([])
const tempFilters = computed(() => {
  return { ...logsStore.filters }
})

watch(() => route, logsStore.fetchSyncLogs, { immediate: true })

const openDetailModal = (syncLog) => {
  tabs.value = []
  tabs.value.push({
    title: 'Data',
    content: syncLog,
    value: '0'
  })
  if (['Failed', 'Skipped'].includes(syncLog.status)) {
    tabs.value.push({
      title: 'Error',
      content: syncLog.errordetails,
      id: syncLog.id,
      showRetry: syncLog.status === 'Failed',
      value: '1'
    })
  }
  showDetailModal.value = true
}

const mapStatusIcon = (status) => {
  if (['Processing'].includes(status)) return 'pi pi-spin pi-spinner'
  return ''
}

const mapStatusSeveriy = (status) => {
  if (status === 'Completed') return 'success'
  if (status === 'Failed') return 'danger'
  if (['New', 'Enqueued', 'Processing'].includes(status)) return 'info'
  return 'warn'
}

const mapStatusValue = (status) => {
  if (status === 'Completed') return 'OK'
  if (status === 'Failed') return 'KO'
  return status
}
const op = ref()
const toggle = (event) => {
  op.value.toggle(event)
}
const applyFilter = async () => {
  Object.keys(tempFilters.value).forEach(k => { logsStore.filters[k] = tempFilters.value[k] })
  await logsStore.fetchSyncLogs()
}

</script>

<template>
  <Dialog
    v-model:visible="showDetailModal"
    :position="windowIsTooSmall ? 'center' : 'right'"
    :dismissable-mask="true"
    content-style="overflow-y: hidden; display: flex; flex-direction: column"
    modal
    header="Sync Log Detail"
    :style="{maxWidth: windowIsTooSmall ? '90vw' : '60vw', minWidth: '35vw', maxHeight: windowIsTooSmall ? '90vh' : '60vh' }"
  >
    <Tabs
      class="max-h-full flex min-h-0"
      value="0"
    >
      <TabList class="top-0">
        <Tab
          v-for="tab in tabs"
          :key="tab.title"
          :value="tab.value"
        >
          {{ tab.title }}
        </Tab>
      </TabList>
      <TabPanels class="flex flex-grow min-h-0">
        <TabPanel
          v-for="tab in tabs"
          :key="tab.content"
          :value="tab.value"
          class="w-full flex flex-col"
        >
          <highlightjs
            language="json"
            :code="JSON.stringify(tab.content, null, 2)"
            class="overflow-y-auto h-auto"
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  </Dialog>
  <div class="card">
    <LogFilter
      v-if="!windowIsTooSmall"
      v-model="logsStore.filters"
    />
    <Divider v-if="!windowIsTooSmall" />
    <div
      v-if="loading"
      class="flex flex-1 w-full items-center justify-center"
    >
      <ProgressSpinner />
    </div>
    <div
      v-else
      class="flex-1 overflow-y-auto px-0 -mx-4 -mb-4 md:mb-0 md:mx-0 md:px-10 md:pb-4 rounded-lg bg-[--p-content-background]"
    >
      <DataTable
        v-if="!windowIsTooSmall"
        selection-mode="single"
        :value="logsStore.syncLogs"
        table-style="min-width: 50rem; width: 100%"
        scrollable
        scroll-height="flex"
        :virtual-scroller-options="{ itemSize: 47, numToleratedItems: 50 }"
        @row-select="openDetailModal($event.data)"
      >
        <template #empty>
          No Sync Logs found
        </template>
        <Column
          style="min-width: 10rem"
          field="code"
          header="Status"
        >
          <template #body="{ data }">
            <Tag
              :icon="mapStatusIcon(data.status)"
              :severity="mapStatusSeveriy(data.status)"
              :value="mapStatusValue(data.status)"
            />
          </template>
        </Column>
        <Column
          field="object"
          header="Object"
        />
        <Column
          field="createddate"
          header="Created Date"
          style="width: 200px;"
        >
          <template #body="{ data }">
            {{ new Date(data.createddate).toLocaleString([], {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'}) }}
          </template>
        </Column>
        <Column
          field="formattedDuration"
          header="Duration"
        />
        <Column header="Inserted">
          <template #body="{ data }">
            {{ Intl.NumberFormat().format(data.insertedrecordcount) }}
          </template>
        </Column>
        <Column header="Updated">
          <template #body="{ data }">
            {{ Intl.NumberFormat().format(data.updatedrecordcount) }}
          </template>
        </Column>
        <Column header="Deleted">
          <template #body="{ data }">
            {{ Intl.NumberFormat().format(data.deletedrecordcount) }}
          </template>
        </Column>
      </DataTable>

      <DataTable
        v-else
        selection-mode="single"
        :value="logsStore.syncLogs"
        table-style="width: 100%"
        scrollable
        scroll-height="flex"
        :virtual-scroller-options="{ itemSize: 82.5, numToleratedItems: 50 }"
        @row-select="openDetailModal($event.data)"
      >
        <template #empty>
          No Sync Logs found
        </template>
        <Column
          field="startdate"
          sortable
        >
          <template #header>
            <div class="flex flex-row w-full items-center justify-between">
              <b>Sync Logs</b>
              <Button
                type="button"
                icon="pi pi-filter"
                variant="text"
                @click="toggle"
              />
              <Popover ref="op">
                <LogFilter v-model="tempFilters" />
                <div class="flex flex-row justify-end pt-3">
                  <Button
                    label="Apply"
                    @click="applyFilter()"
                  />
                </div>
              </Popover>
            </div>
          </template>
          <template #body="{ data }">
            <div class="flex items-center justify-between w-full">
              <div class="flex flex-col items-start gap-1">
                <div class="flex flex-row items-center gap-2">
                  <b>{{ data.object }}</b>
                </div>
                <div class="flex flex-row items-center gap-2 w-full">
                  <i
                    class="pi pi-clock"
                  />
                  <div class="flex flex-row items-center h-full">
                    {{ new Date(data.createddate).toLocaleString([], {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'}) }}
                  </div>
                </div>
                <div class="flex flex-row items-center text-sm text-surface-500 dark:text-surface-400 w-full gap-2">
                  <div class="flex flex-row items-center gap-1 pr-2">
                    <i class="pi pi-stopwatch" />
                    {{ data.formattedDuration }}
                  </div>
                  <div
                    v-if="data.insertedrecordcount > 0"
                    class="flex flex-row items-center gap-1"
                  >
                    <i class="pi pi-plus" />
                    <b>{{ Intl.NumberFormat().format(data.insertedrecordcount) }}</b>
                  </div>
                  <div
                    v-if="data.updatedrecordcount > 0"
                    class="flex flex-row items-center gap-1"
                  >
                    <i class="pi pi-sync" />
                    <b>{{ Intl.NumberFormat().format(data.updatedrecordcount) }}</b>
                  </div>
                  <div
                    v-if="data.deletedrecordcount > 0"
                    class="flex flex-row items-center gap-1"
                  >
                    <i class="pi pi-minus" />
                    <b>{{ Intl.NumberFormat().format(data.deletedrecordcount) }}</b>
                  </div>
                </div>
              </div>
              <Tag
                :icon="mapStatusIcon(data.status)"
                :severity="mapStatusSeveriy(data.status)"
                :value="mapStatusValue(data.status)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>
<style scoped>
.p-tabpanels {
  flex-grow: 1;
}
</style>
